// src/features/tuition/components/online/TutorialForm.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  FormHelperText,
  Grid,
  Tabs,
  Tab,
  Autocomplete,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  VideoLibrary as VideoIcon,
  Image as ImageIcon,
  Description as DocumentIcon,
  Save as SaveIcon,
  Publish as PublishIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { db, storage } from '../../../../firebase/config';
import MicroActivityList from './MicroActivityList';
import MicroActivityForm from './MicroActivityForm';

const TutorialForm = ({ open, onClose, initialData = null, subjectId, userId }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    grade: '',
    duration: 30,
    description: '',
    content: '',
    thumbnail: null,
    video: null,
    documents: [],
    images: [],
    microActivities: [],
    status: 'draft', // 'draft', 'published', 'unpublished'
    publishedAt: null
  });
  
  const [subjects, setSubjects] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState('');
  const [documentFiles, setDocumentFiles] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [isAddingActivity, setIsAddingActivity] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Reference for the file inputs
  const thumbnailInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const imageInputRef = useRef(null);
  
  // React Quill modules and formats
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };
  
  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'script',
    'indent',
    'color', 'background',
    'link', 'image', 'video'
  ];
  
  useEffect(() => {
    if (open) {
      // Load subjects
      loadSubjects();
      
      if (initialData) {
        setFormData({
          title: initialData.title || '',
          subject: initialData.subject || '',
          grade: initialData.grade || '',
          duration: initialData.duration || 30,
          description: initialData.description || '',
          content: initialData.content || '',
          thumbnail: initialData.thumbnail || null,
          video: initialData.video || null,
          documents: initialData.documents || [],
          images: initialData.images || [],
          microActivities: initialData.microActivities || [],
          status: initialData.status || 'draft',
          publishedAt: initialData.publishedAt || null
        });
        
        if (initialData.thumbnail) {
          setThumbnailPreview(initialData.thumbnail.url);
        }
        
        if (initialData.video) {
          setVideoPreview(initialData.video.url);
        }
      } else {
        resetForm();
      }
    }
  }, [initialData, open, subjectId]);
  
  const loadSubjects = async () => {
    try {
      // In a real app, you would fetch subjects from the database
      // For now, let's use mock data
      const mockSubjects = [
        { id: 's1', name: 'Mathematics', grade: '10' },
        { id: 's2', name: 'Physical Sciences', grade: '11' },
        { id: 's3', name: 'English Language', grade: '9' },
        { id: 's4', name: 'Biology', grade: '12' }
      ];
      
      setSubjects(mockSubjects);
    } catch (error) {
      console.error("Error loading subjects:", error);
    }
  };
  
  const resetForm = () => {
    setFormData({
      title: '',
      subject: '',
      grade: '',
      duration: 30,
      description: '',
      content: '',
      thumbnail: null,
      video: null,
      documents: [],
      images: [],
      microActivities: [],
      status: 'draft',
      publishedAt: null
    });
    setThumbnailFile(null);
    setThumbnailPreview('');
    setVideoFile(null);
    setVideoPreview('');
    setDocumentFiles([]);
    setImageFiles([]);
    setErrors({});
    setActiveTab(0);
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const handleQuillChange = (content) => {
    setFormData(prev => ({
      ...prev,
      content
    }));
    
    // Clear error if needed
    if (errors.content) {
      setErrors(prev => ({
        ...prev,
        content: ''
      }));
    }
  };
  
  const handleFileChange = async (e, fileType) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    
    // Validate file
    const validationResult = validateFile(file, fileType);
    if (!validationResult.valid) {
      setErrors(prev => ({
        ...prev,
        [fileType]: validationResult.error
      }));
      return;
    }
    
    // Process file based on type
    switch (fileType) {
      case 'thumbnail':
        setThumbnailFile(file);
        generatePreview(file, setThumbnailPreview);
        break;
      case 'video':
        setVideoFile(file);
        setVideoPreview(URL.createObjectURL(file));
        break;
      case 'document':
        setDocumentFiles([...documentFiles, file]);
        break;
      case 'image':
        setImageFiles([...imageFiles, file]);
        generatePreview(file, (preview) => {
          setImageFiles(prev => [...prev.filter(f => f !== file), { file, preview }]);
        });
        break;
      default:
        break;
    }
    
    // Clear error
    if (errors[fileType]) {
      setErrors(prev => ({
        ...prev,
        [fileType]: ''
      }));
    }
  };
  
  const validateFile = (file, fileType) => {
    // Define file validation rules
    const rules = {
      thumbnail: {
        types: ['image/jpeg', 'image/png', 'image/jpg'],
        maxSize: 5 * 1024 * 1024, // 5MB
        error: 'Thumbnail must be a JPEG or PNG image less than 5MB'
      },
      video: {
        types: ['video/mp4', 'video/webm', 'video/ogg'],
        maxSize: 100 * 1024 * 1024, // 100MB
        error: 'Video must be MP4, WebM, or OGG format less than 100MB'
      },
      document: {
        types: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        maxSize: 20 * 1024 * 1024, // 20MB
        error: 'Document must be PDF or Word format less than 20MB'
      },
      image: {
        types: ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'],
        maxSize: 10 * 1024 * 1024, // 10MB
        error: 'Image must be JPEG, PNG, or GIF format less than 10MB'
      }
    };
    
    const rule = rules[fileType];
    
    if (!rule.types.includes(file.type)) {
      return { valid: false, error: rule.error };
    }
    
    if (file.size > rule.maxSize) {
      return { valid: false, error: rule.error };
    }
    
    return { valid: true };
  };
  
  const generatePreview = (file, setPreviewFunction) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewFunction(e.target.result);
    };
    reader.readAsDataURL(file);
  };
  
  const removeFile = (fileType, index = null) => {
    switch (fileType) {
      case 'thumbnail':
        setThumbnailFile(null);
        setThumbnailPreview('');
        setFormData(prev => ({ ...prev, thumbnail: null }));
        break;
      case 'video':
        setVideoFile(null);
        setVideoPreview('');
        setFormData(prev => ({ ...prev, video: null }));
        break;
      case 'document':
        if (index !== null) {
          setDocumentFiles(prev => prev.filter((_, i) => i !== index));
          setFormData(prev => ({
            ...prev,
            documents: prev.documents.filter((_, i) => i !== index)
          }));
        }
        break;
      case 'image':
        if (index !== null) {
          setImageFiles(prev => prev.filter((_, i) => i !== index));
          setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
          }));
        }
        break;
      default:
        break;
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.subject) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.grade) {
      newErrors.grade = 'Grade is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }
    
    if (!thumbnailFile && !formData.thumbnail) {
      newErrors.thumbnail = 'Thumbnail image is required';
    }
    
    setErrors(newErrors);
    
    return Object.keys(newErrors).length === 0;
  };
  
  const uploadFiles = async () => {
    try {
      const uploads = {};
      
      // Upload thumbnail
      if (thumbnailFile) {
        const thumbnailData = await uploadFile(thumbnailFile, 'thumbnails', 'thumbnail');
        uploads.thumbnail = thumbnailData;
      } else if (formData.thumbnail) {
        uploads.thumbnail = formData.thumbnail;
      }
      
      // Upload video
      if (videoFile) {
        const videoData = await uploadFile(videoFile, 'videos', 'video');
        uploads.video = videoData;
      } else if (formData.video) {
        uploads.video = formData.video;
      }
      
      // Upload documents
      const documentUploads = [];
      for (const docFile of documentFiles) {
        if (!docFile.uploaded) {
          const docData = await uploadFile(docFile, 'documents', 'document');
          documentUploads.push(docData);
        }
      }
      uploads.documents = [
        ...formData.documents,
        ...documentUploads
      ];
      
      // Upload images
      const imageUploads = [];
      for (const imgFile of imageFiles) {
        if (!imgFile.uploaded) {
          const imgData = await uploadFile(imgFile.file, 'images', 'image');
          imageUploads.push(imgData);
        }
      }
      uploads.images = [
        ...formData.images,
        ...imageUploads
      ];
      
      return uploads;
    } catch (error) {
      console.error("Error uploading files:", error);
      throw new Error("Failed to upload files: " + error.message);
    }
  };
  
  const uploadFile = async (file, folder, fileType) => {
    const storageRef = ref(storage, `tutorials/${userId}/${folder}/${file.name}_${Date.now()}`);
    
    // Track upload progress
    setUploadProgress(prev => ({
      ...prev,
      [fileType]: 0
    }));
    
    // Create upload task
    const uploadTask = uploadBytes(storageRef, file);
    
    // Wait for upload to complete
    await uploadTask;
    
    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);
    
    // Update progress
    setUploadProgress(prev => ({
      ...prev,
      [fileType]: 100
    }));
    
    return {
      name: file.name,
      type: file.type,
      size: file.size,
      url: downloadURL,
      path: storageRef.fullPath,
      uploadedAt: new Date().toISOString()
    };
  };
  
  const handleSubmit = async (action = 'save') => {
    // Validate form
    if (!validateForm()) {
      // Scroll to the tab with the first error
      if (errors.title || errors.subject || errors.grade || errors.description) {
        setActiveTab(0);
      } else if (errors.content) {
        setActiveTab(1);
      } else if (errors.thumbnail || errors.video) {
        setActiveTab(2);
      }
      return;
    }
    
    setLoading(true);
    
    try {
      // Upload files
      const uploads = await uploadFiles();
      
      // Prepare tutorial data
      const tutorialData = {
        title: formData.title,
        subject: formData.subject,
        grade: formData.grade,
        duration: parseInt(formData.duration),
        description: formData.description,
        content: formData.content,
        thumbnail: uploads.thumbnail,
        video: uploads.video,
        documents: uploads.documents,
        images: uploads.images,
        microActivities: formData.microActivities,
        updatedAt: new Date().toISOString(),
        userId
      };
      
      // Update status based on action
      if (action === 'publish') {
        tutorialData.status = 'published';
        tutorialData.publishedAt = new Date().toISOString();
      } else if (action === 'unpublish') {
        tutorialData.status = 'unpublished';
      } else {
        tutorialData.status = formData.status;
      }
      
      if (initialData && initialData.id) {
        // Update existing tutorial
        console.log("Updating tutorial:", tutorialData);
        // In a real app, you would call an API or Firestore to update the tutorial
        // await updateTutorial(initialData.id, tutorialData);
        
        setNotification({
          open: true,
          message: `Tutorial successfully ${action === 'publish' ? 'published' : action === 'unpublish' ? 'unpublished' : 'updated'}!`,
          severity: 'success'
        });
      } else {
        // Create new tutorial
        console.log("Creating new tutorial:", tutorialData);
        // In a real app, you would call an API or Firestore to create the tutorial
        // const newTutorial = await createTutorial(tutorialData);
        
        setNotification({
          open: true,
          message: `Tutorial successfully ${action === 'publish' ? 'published' : 'created'}!`,
          severity: 'success'
        });
      }
      
      resetForm();
      
      // Close form after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error("Error saving tutorial:", error);
      setNotification({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddActivity = () => {
    setIsAddingActivity(true);
    setSelectedActivity(null);
  };
  
  const handleEditActivity = (activity) => {
    setSelectedActivity(activity);
    setIsAddingActivity(true);
  };
  
  const handleDeleteActivity = (index) => {
    setFormData(prev => ({
      ...prev,
      microActivities: prev.microActivities.filter((_, i) => i !== index)
    }));
  };
  
  const handleSaveActivity = (activity) => {
    if (selectedActivity) {
      // Edit existing activity
      const index = formData.microActivities.findIndex(a => a.id === selectedActivity.id);
      if (index !== -1) {
        const updatedActivities = [...formData.microActivities];
        updatedActivities[index] = activity;
        setFormData(prev => ({
          ...prev,
          microActivities: updatedActivities
        }));
      }
    } else {
      // Add new activity
      setFormData(prev => ({
        ...prev,
        microActivities: [...prev.microActivities, { ...activity, id: Date.now().toString() }]
      }));
    }
    
    setIsAddingActivity(false);
    setSelectedActivity(null);
  };
  
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };
  
  const getSubjectById = (id) => {
    return subjects.find(subject => subject.id === id) || {};
  };
  
  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? null : onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ 
          sx: { borderRadius: 2 } 
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              {initialData ? 'Edit Tutorial' : 'Create New Tutorial'}
            </Typography>
            <IconButton 
              edge="end" 
              color="inherit" 
              onClick={onClose}
              disabled={loading}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              aria-label="tutorial form tabs"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 48
                },
                '& .Mui-selected': {
                  color: THEME_COLORS.primary
                }
              }}
            >
              <Tab label="Basic Info" />
              <Tab label="Content" />
              <Tab label="Media" />
              <Tab label="Activities" />
            </Tabs>
          </Box>
          
          {/* Basic Info Tab */}
          <Box sx={{ p: 3, display: activeTab === 0 ? 'block' : 'none' }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tutorial Title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  error={!!errors.title}
                  helperText={errors.title}
                  disabled={loading}
                  sx={{ mb: 2 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.subject} disabled={loading}>
                  <InputLabel>Subject</InputLabel>
                  <Select
                    name="subject"
                    value={formData.subject}
                    label="Subject"
                    onChange={handleInputChange}
                    required
                  >
                    <MenuItem value="">Select a subject</MenuItem>
                    {subjects.map(subject => (
                      <MenuItem key={subject.id} value={subject.id}>
                        {subject.name} - Grade {subject.grade}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.subject && <FormHelperText>{errors.subject}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Grade"
                  name="grade"
                  value={formData.grade || getSubjectById(formData.subject).grade || ''}
                  onChange={handleInputChange}
                  required
                  error={!!errors.grade}
                  helperText={errors.grade}
                  disabled={!!formData.subject || loading}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duration (minutes)"
                  name="duration"
                  type="number"
                  value={formData.duration}
                  onChange={handleInputChange}
                  inputProps={{ min: 1 }}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={loading}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    label="Status"
                    onChange={handleInputChange}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="unpublished">Unpublished</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={4}
                  required
                  error={!!errors.description}
                  helperText={errors.description}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </Box>
          
          {/* Content Tab */}
          <Box sx={{ p: 3, display: activeTab === 1 ? 'block' : 'none' }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Tutorial Content
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Use the rich text editor below to create your tutorial content. You can add formatting, links, and images.
            </Typography>
            
            <div style={{ height: 'calc(100vh - 400px)', minHeight: '300px' }}>
              <ReactQuill
                value={formData.content}
                onChange={handleQuillChange}
                modules={quillModules}
                formats={quillFormats}
                style={{ height: 'calc(100% - 42px)' }}
                placeholder="Start writing your tutorial content here..."
              />
            </div>
            
            {errors.content && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {errors.content}
              </Typography>
            )}
          </Box>
          
          {/* Media Tab */}
          <Box sx={{ p: 3, display: activeTab === 2 ? 'block' : 'none' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Thumbnail Image
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Upload a thumbnail image for your tutorial. Recommended size: 1280 x 720 pixels.
                  </Typography>
                  
                  {thumbnailPreview ? (
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <img
                        src={thumbnailPreview}
                        alt="Thumbnail preview"
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: '200px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0'
                        }}
                      />
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(0,0,0,0.5)',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.7)',
                          }
                        }}
                        onClick={() => removeFile('thumbnail')}
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        border: '2px dashed #e0e0e0',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'rgba(0,0,0,0.02)',
                        mb: 2
                      }}
                    >
                      <input
                        ref={thumbnailInputRef}
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="thumbnail-image-upload"
                        type="file"
                        onChange={(e) => handleFileChange(e, 'thumbnail')}
                        disabled={loading}
                      />
                      <label htmlFor="thumbnail-image-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          onClick={() => thumbnailInputRef.current.click()}
                          sx={{
                            borderColor: THEME_COLORS.primary,
                            color: THEME_COLORS.primary,
                            '&:hover': {
                              borderColor: THEME_COLORS.primary,
                              bgcolor: 'rgba(39, 35, 97, 0.04)'
                            }
                          }}
                          disabled={loading}
                        >
                          Upload Thumbnail
                        </Button>
                      </label>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        JPEG or PNG, up to 5MB
                      </Typography>
                    </Box>
                  )}
                  
                  {errors.thumbnail && (
                    <Typography variant="body2" color="error">
                      {errors.thumbnail}
                    </Typography>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Tutorial Video (Optional)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Upload a video for your tutorial. Supported formats: MP4, WebM, OGG.
                  </Typography>
                  
                  {videoPreview ? (
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <video
                        src={videoPreview}
                        controls
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: '200px',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0'
                        }}
                      />
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(0,0,0,0.5)',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.7)',
                          }
                        }}
                        onClick={() => removeFile('video')}
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        border: '2px dashed #e0e0e0',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'rgba(0,0,0,0.02)',
                        mb: 2
                      }}
                    >
                      <input
                        ref={videoInputRef}
                        accept="video/*"
                        style={{ display: 'none' }}
                        id="video-upload"
                        type="file"
                        onChange={(e) => handleFileChange(e, 'video')}
                        disabled={loading}
                      />
                      <label htmlFor="video-upload">
                      <Button
                          variant="outlined"
                          component="span"
                          startIcon={<VideoIcon />}
                          onClick={() => videoInputRef.current.click()}
                          sx={{
                            borderColor: THEME_COLORS.primary,
                            color: THEME_COLORS.primary,
                            '&:hover': {
                              borderColor: THEME_COLORS.primary,
                              bgcolor: 'rgba(39, 35, 97, 0.04)'
                            }
                          }}
                          disabled={loading}
                        >
                          Upload Video
                        </Button>
                      </label>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        MP4, WebM, or OGG, up to 100MB
                      </Typography>
                    </Box>
                  )}
                  
                  {errors.video && (
                    <Typography variant="body2" color="error">
                      {errors.video}
                    </Typography>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Supporting Documents (Optional)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Upload supporting documents such as PDFs, Word files, etc.
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    {formData.documents.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        {formData.documents.map((doc, index) => (
                          <Paper
                            key={index}
                            variant="outlined"
                            sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                          >
                            <Box display="flex" alignItems="center">
                              <DocumentIcon sx={{ mr: 1, color: THEME_COLORS.primary }} />
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {doc.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatFileSize(doc.size)}
                                </Typography>
                              </Box>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => removeFile('document', index)}
                              disabled={loading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Paper>
                        ))}
                      </Box>
                    )}
                    
                    {documentFiles.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        {documentFiles.map((file, index) => (
                          <Paper
                            key={index}
                            variant="outlined"
                            sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                          >
                            <Box display="flex" alignItems="center">
                              <DocumentIcon sx={{ mr: 1, color: THEME_COLORS.primary }} />
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {file.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatFileSize(file.size)}
                                </Typography>
                              </Box>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => removeFile('document', index)}
                              disabled={loading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Paper>
                        ))}
                      </Box>
                    )}
                    
                    <Button
                      variant="outlined"
                      startIcon={<DocumentIcon />}
                      onClick={() => documentInputRef.current.click()}
                      sx={{
                        borderColor: THEME_COLORS.primary,
                        color: THEME_COLORS.primary,
                        '&:hover': {
                          borderColor: THEME_COLORS.primary,
                          bgcolor: 'rgba(39, 35, 97, 0.04)'
                        }
                      }}
                      disabled={loading}
                    >
                      Add Document
                    </Button>
                    <input
                      ref={documentInputRef}
                      accept=".pdf,.doc,.docx"
                      style={{ display: 'none' }}
                      id="document-upload"
                      type="file"
                      onChange={(e) => handleFileChange(e, 'document')}
                      disabled={loading}
                    />
                  </Box>
                  
                  {errors.document && (
                    <Typography variant="body2" color="error">
                      {errors.document}
                    </Typography>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Additional Images (Optional)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Upload additional images to include in your tutorial.
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Grid container spacing={1}>
                      {formData.images.map((img, index) => (
                        <Grid item xs={6} sm={4} key={index}>
                          <Box
                            sx={{
                              position: 'relative',
                              height: 120,
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: '1px solid #e0e0e0'
                            }}
                          >
                            <img 
                              src={img.url} 
                              alt={`Additional ${index + 1}`} 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover' 
                              }}
                            />
                            <IconButton
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                p: 0.5,
                                '&:hover': {
                                  bgcolor: 'rgba(0,0,0,0.7)',
                                }
                              }}
                              onClick={() => removeFile('image', index)}
                              disabled={loading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                      
                      {imageFiles.map((img, index) => (
                        <Grid item xs={6} sm={4} key={`new-${index}`}>
                          <Box
                            sx={{
                              position: 'relative',
                              height: 120,
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: '1px solid #e0e0e0'
                            }}
                          >
                            <img 
                              src={img.preview} 
                              alt={`New ${index + 1}`} 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover' 
                              }}
                            />
                            <IconButton
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                p: 0.5,
                                '&:hover': {
                                  bgcolor: 'rgba(0,0,0,0.7)',
                                }
                              }}
                              onClick={() => removeFile('image', index)}
                              disabled={loading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                      
                      <Grid item xs={6} sm={4}>
                        <Box
                          sx={{
                            height: 120,
                            border: '2px dashed #e0e0e0',
                            borderRadius: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                              borderColor: THEME_COLORS.primary,
                              bgcolor: 'rgba(39, 35, 97, 0.04)'
                            }
                          }}
                          onClick={() => imageInputRef.current.click()}
                        >
                          <AddIcon sx={{ mb: 1, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            Add Image
                          </Typography>
                        </Box>
                        <input
                          ref={imageInputRef}
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="image-upload"
                          type="file"
                          onChange={(e) => handleFileChange(e, 'image')}
                          disabled={loading}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  
                  {errors.image && (
                    <Typography variant="body2" color="error">
                      {errors.image}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          {/* Activities Tab */}
          <Box sx={{ p: 3, display: activeTab === 3 ? 'block' : 'none' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Micro Activities
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add interactive activities to engage your students and track their progress.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddActivity}
                sx={{
                  bgcolor: THEME_COLORS.primary,
                  '&:hover': { bgcolor: '#1e1c4e' }
                }}
                disabled={loading}
              >
                Add Activity
              </Button>
            </Box>
            
            <MicroActivityList 
              activities={formData.microActivities}
              onEditActivity={handleEditActivity}
              onDeleteActivity={handleDeleteActivity}
              disabled={loading}
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={onClose} 
            disabled={loading}
          >
            Cancel
          </Button>
          
          {formData.status !== 'published' && (
            <Button
              variant="outlined"
              onClick={() => handleSubmit('publish')}
              disabled={loading}
              startIcon={<PublishIcon />}
              sx={{
                borderColor: '#4CAF50',
                color: '#4CAF50',
                '&:hover': {
                  borderColor: '#388E3C',
                  bgcolor: 'rgba(76, 175, 80, 0.04)'
                }
              }}
            >
              Publish
            </Button>
          )}
          
          {formData.status === 'published' && (
            <Button
              variant="outlined"
              onClick={() => handleSubmit('unpublish')}
              disabled={loading}
              sx={{
                borderColor: '#FF9800',
                color: '#FF9800',
                '&:hover': {
                  borderColor: '#F57C00',
                  bgcolor: 'rgba(255, 152, 0, 0.04)'
                }
              }}
            >
              Unpublish
            </Button>
          )}
          
          <Button
            variant="contained"
            onClick={() => handleSubmit('save')}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': { bgcolor: '#1e1c4e' }
            }}
          >
            {loading ? 'Saving...' : initialData ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Notification */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
      {/* Activity Form Dialog */}
      <MicroActivityForm
        open={isAddingActivity}
        onClose={() => setIsAddingActivity(false)}
        onSave={handleSaveActivity}
        initialData={selectedActivity}
      />
    </>
  );
};

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) {
    return bytes + ' bytes';
  } else if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(1) + ' KB';
  } else {
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }
};

export default TutorialForm;